import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Youtube, Facebook, Linkedin, Mail, Phone, MapPin } from "lucide-react";
import { useCoachContext } from "@/contexts/CoachContext";
import { supabase } from "@/integrations/supabase/client";
import defaultLogo from "@/assets/gocoachify-logo.webp";

const Footer = () => {
  const { coachId } = useCoachContext();
  const currentYear = new Date().getFullYear();
  const [customLogo, setCustomLogo] = useState<string | null>(null);
  const [businessName, setBusinessName] = useState<string>("GoCoachify");

  useEffect(() => {
    if (coachId) {
      const fetchCoachData = async () => {
        const { data } = await supabase.from("coaches").select("logo_url, business_name").eq("id", coachId).single();
        if (data) { setCustomLogo(data.logo_url); setBusinessName(data.business_name || "GoCoachify"); }
      };
      fetchCoachData();
    }
  }, [coachId]);

  const socialLinks = [
    { icon: Youtube, href: "https://www.youtube.com/@gocoachifyai", label: "YouTube" },
    { icon: Facebook, href: "https://www.facebook.com/gocoachifyai", label: "Facebook" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/gocoachifyai", label: "LinkedIn" },
  ];

  const navLinks = coachId ? [
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#packages", label: "Packages" },
    { href: "#contact", label: "Contact" },
  ] : [
    { href: "/about", label: "About", isRoute: true },
    { href: "/blog", label: "Blog", isRoute: true },
    { href: "/press", label: "Press", isRoute: true },
    { href: "/contact", label: "Contact", isRoute: true },
    { href: "/partners", label: "Partner with Us", isRoute: true },
    { href: "/connect", label: "Connect an AI assistant", isRoute: true },
    { href: "/terms", label: "Terms of Service", isRoute: true },
    { href: "/privacy-policy", label: "Privacy Policy", isRoute: true },
  ];

  return (
    <footer className="bg-muted border-t border-border py-10 text-foreground">
      <div className="max-w-[980px] mx-auto px-4 md:px-6">
        {/* Links row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-8">
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-3">GoCoachify</h4>
            <p className="text-xs text-muted-foreground leading-relaxed">
              The AI-powered platform for coaches.
            </p>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-3">Quick Links</h4>
            <nav className="flex flex-col gap-1.5">
              {navLinks.slice(0, 4).map((link) => (
                'isRoute' in link && link.isRoute ? (
                  <Link key={link.href} to={link.href} className="text-xs text-muted-foreground hover:text-foreground transition-colors">{link.label}</Link>
                ) : (
                  <a key={link.href} href={link.href} className="text-xs text-muted-foreground hover:text-foreground transition-colors">{link.label}</a>
                )
              ))}
            </nav>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-3">Toolkit Categories</h4>
            <nav className="flex flex-col gap-1.5">
              <Link to="/toolkit/wheel-of-life" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Wheel of Life</Link>
              <Link to="/toolkit/cbt-reframe" className="text-xs text-muted-foreground hover:text-foreground transition-colors">CBT Reframe</Link>
              <Link to="/toolkit/smart-goals" className="text-xs text-muted-foreground hover:text-foreground transition-colors">SMART Goals</Link>
              <Link to="/toolkit/adhd-screening" className="text-xs text-muted-foreground hover:text-foreground transition-colors">ADHD Screening</Link>
            </nav>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-3">Legal</h4>
            <nav className="flex flex-col gap-1.5">
              <Link to="/terms" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Terms of Service</Link>
              <Link to="/privacy-policy" className="text-xs text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</Link>
            </nav>
          </div>
          <div>
            <h4 className="text-xs font-semibold text-foreground mb-3">Connect</h4>
            <a href="mailto:hello@gocoachify.com" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-1.5">
              <Mail className="w-3 h-3" /> hello@gocoachify.com
            </a>
            <a href="tel:+16722730071" className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-3">
              <Phone className="w-3 h-3" /> +1 (672) 273-0071
            </a>
            <div className="flex gap-2">
              {socialLinks.map((link, index) => (
                <a key={index} href={link.href} target="_blank" rel="noopener noreferrer"
                  className="w-7 h-7 rounded-full bg-foreground/10 flex items-center justify-center text-muted-foreground hover:text-foreground transition-all"
                  aria-label={link.label}>
                  <link.icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-2">
              <MapPin className="w-3 h-3" /> Headquarters: Canada
            </p>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-5 border-t border-border text-center">
          <p className="text-[11px] text-muted-foreground">
            Copyright © {currentYear} {coachId ? businessName : "GoCoachify"}. All rights reserved.
          </p>
          <p className="text-[10px] text-muted-foreground/70 mt-2">
            Platform developed by{" "}
            <a href="https://nexamartech.com/" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors underline">
              NexamarTech
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
